import { formFieldFocus } from '@wix/bi-logger-forms-platform-uou/v2';
import { groupErrorsByTarget } from '@wix/form-validator';
import React, { useCallback, useMemo, useRef } from 'react';
import { useBi } from '../../../../contexts/bi-context';
import { FieldAttributesContext } from '../../../../contexts/field-attributes-context';
import { FieldPropsContext } from '../../../../contexts/field-props-context';
import { useFieldValidation } from '../../../../contexts/field-validation-context';
import { useForm } from '../../../../contexts/form-context';
import { useError } from '../../../../field-error';
import { FIELD_VIEWERS } from '../../../../field-viewer/field-viewers';
import { useFieldAttributes } from '../../../../services/field-attributes';
import { useExternalData } from '../../contexts/external-data-context';
import { useFormFields } from '../../contexts/form-fields-context';
export const InputField = ({ field, errors, values, onFieldChange, onBlur, onFocus, FieldLayout })=>{
    const bi = useBi();
    const componentsById = useFormFields();
    const externalData = useExternalData();
    const { validateFields } = useFieldValidation();
    const form = useForm();
    const fieldValue = values[field.target];
    const fieldValueRef = useRef(fieldValue);
    fieldValueRef.current = fieldValue;
    const errorsByTarget = useMemo(()=>groupErrorsByTarget(errors), [
        errors
    ]);
    const fieldErrors = errorsByTarget?.[field.target];
    const fieldError = fieldErrors?.[0];
    const { requiredIndicator, requiredIndicatorPlacement } = form.requiredIndicatorProperties || {};
    const _onBlur = useCallback(()=>{
        const currentFieldValue = fieldValueRef.current;
        bi?.report(formFieldFocus({
            action_type: 'blur',
            field_id: field.id,
            field_label: field.properties.label,
            field_target: field?.target
        }));
        validateFields([
            {
                value: currentFieldValue,
                path: field.path
            }
        ]);
        onBlur?.(field.path);
    }, [
        bi,
        field.id,
        field.properties.label,
        field.target,
        field.path,
        validateFields,
        onBlur
    ]);
    const _onFocus = ()=>{
        bi?.report(formFieldFocus({
            action_type: 'focus',
            field_id: field.id,
            field_label: field.properties.label,
            field_target: field?.target
        }));
        onFocus?.(field.path);
    };
    const handleChange = useCallback((value)=>{
        if (fieldError) {
            validateFields([
                {
                    value,
                    path: field.path
                }
            ]);
        }
        if (!value && value !== 0 && typeof value !== 'boolean' || Array.isArray(value) && value.length === 0) {
            value = null;
        }
        onFieldChange?.(field.target, value);
    }, [
        onFieldChange,
        field,
        fieldError,
        validateFields
    ]);
    const { hasError, errorMessage } = useError({
        error: fieldError,
        customErrors: field.properties?.customErrors,
        fieldType: field.fieldType,
        properties: field.properties,
        validationError: fieldError
    });
    const FieldComponent = componentsById[field.fieldType];
    const fieldProps = {
        ...field.properties,
        readOnly: Boolean(field.properties?.readOnly),
        onChange: handleChange,
        onBlur: _onBlur,
        onFocus: _onFocus,
        onFieldBlur: onBlur,
        onFieldFocus: onFocus,
        hasError,
        errorMessage,
        error: fieldError,
        errors: fieldErrors,
        value: fieldValue,
        id: field.id,
        target: field.target,
        path: field.path,
        externalData,
        fieldType: field.fieldType,
        FieldLayout,
        autoComplete: FIELD_VIEWERS[field.fieldType]?.autoComplete,
        readPrehydrationValue: FIELD_VIEWERS[field.fieldType]?.readPrehydrationValue,
        requiredIndicatorType: requiredIndicator,
        requiredIndicatorPlacement
    };
    const fieldAttributes = useFieldAttributes(fieldProps.id, fieldProps.error, fieldProps.description);
    if (!FieldComponent) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(FieldAttributesContext.Provider, {
        value: fieldAttributes
    }, /*#__PURE__*/ React.createElement(FieldPropsContext.Provider, {
        value: {
            v2: fieldProps
        }
    }, /*#__PURE__*/ React.createElement(FieldComponent, fieldProps)));
};

//# sourceMappingURL=input-field.js.map